import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import MeasuredBy from "../molecules/MeasuredBy";

import { BASE_SIZES } from "../../constants/modal";

import {
  disabledStylesContinue,
  activeStylesContinue,
  femaleBraScreenUI,
  welcomeScreenUI,
  font,
  titleStyles,
} from "../../configs";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { findBrandByDomain } from "../../configs/configLoader";
import StyledSelector from "./components/StyledSelector";
import BaseSizeSelector from "./components/BaseSizeSelector";
import { handleAnalytics } from "../../utils/tracking";
import ShoesComponentSize from "./components/ShoesComponentSize";
import {
  MEDIUM_PADDING_BRANDS,
  SMALL_PADDING_BRANDS,
} from "../../constants/sizes";

import "./index.css";

interface IPropsStepBreasts {
  step: any;
  value_1: {
    current: string | null;
  };
  value_2: {
    current: string | null;
  };
  nextStep: () => void;
  skipResult: () => void;
  sizeCountry: string;
  setSizeCountry: React.Dispatch<React.SetStateAction<string>>;
  subtitleStyles: {
    color: React.CSSProperties["color"];
    fontSize: React.CSSProperties["fontSize"];
    fontWeight: React.CSSProperties["fontWeight"];
    textAlign: React.CSSProperties["textAlign"];
    textTransform: React.CSSProperties["textTransform"];
    justifyContent: React.CSSProperties["justifyContent"];
  };
  isShoesProduct: boolean;
}

type BodyItemsType = "unfocused" | "focused";

const StepBreasts: FC<IPropsStepBreasts> = ({
  step,
  value_1,
  value_2,
  nextStep,
  skipResult,
  sizeCountry,
  setSizeCountry,
  subtitleStyles,
  isShoesProduct,
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const { t } = useTranslation("components/breasts");

  const [selectedSize, setSelectedSize] = useState<any | undefined>();
  const [selectedCup, setSelectedCup] = useState<any | undefined>();

  const [selectedSizeFR, setSelectedSizeFR] = useState<any | undefined>();
  const [selectedCupFR, setSelectedCupFR] = useState<any | undefined>();

  const [activeSizeIndex, setActiveSizeIndex] = useState(0);
  const [activeCupIndex, setActiveCupIndex] = useState(0);
  const [isActiveCupSelection, setIsActiveCupSelection] = useState(false);

  const brandDefined = findBrandByDomain();

  const fontFamily = `${font}, sans-serif`;

  const urlParameters = new URLSearchParams(window.location.search);

  const isLingerie = urlParameters.get("lingerie") === "true";

  useEffect(() => {
    if (selectedSize) {
      const sizeValue =
        sizeCountry === "FR"
          ? (selectedSize?.size as string | null) || null
          : (selectedSize?.fr as string | null) || null;

      setSelectedSizeFR(sizeValue);

      value_1.current = sizeValue ? sizeValue?.toString() : null;
    }

    if (selectedCup) {
      const cupValue =
        sizeCountry === "FR"
          ? (selectedCup?.size as string | null) || null
          : (selectedCup?.fr as string | null) || null;

      setSelectedCupFR(cupValue);

      value_2.current = cupValue ? cupValue?.toString() : null;
    }
  }, [sizeCountry, selectedSize, selectedCup, value_1, value_2]);

  const handlePaddingForBrand = (brandName: string) => {
    const smallPadding = "8px";
    const mediumPadding = "13px";

    if (brandName === "Zadig & Voltaire" || brandName === "Lacoste") {
      return "12px";
    }

    if (SMALL_PADDING_BRANDS.includes(brandName)) {
      return smallPadding;
    } else if (MEDIUM_PADDING_BRANDS.includes(brandName)) {
      return mediumPadding;
    } else {
      return smallPadding;
    }
  };

  const getBodyItemsStyles = (type: BodyItemsType) => {
    const padding = brandDefined?.name
      ? handlePaddingForBrand(brandDefined?.name)
      : "8px";

    const sizeSelectorItem = femaleBraScreenUI.sizeSelector;

    const isSizeUnderlined =
      (sizeSelectorItem["focused"] as any)?.textDecoration === "underline";

    const borderWidth = isSizeUnderlined
      ? "0 0 1px 0"
      : sizeSelectorItem[type].borderWidth;

    const bodyItemsStyles = {
      padding,
      backgroundColor: isSizeUnderlined
        ? "transparent"
        : sizeSelectorItem[type].backgroundColor,
      borderRadius: sizeSelectorItem[type].borderRadius,
      borderWidth,
      color: isSizeUnderlined ? "#000000" : sizeSelectorItem[type].fontColor,
      fontSize: sizeSelectorItem[type].fontSize,
      fontWeight: sizeSelectorItem[type]
        .fontWeight as React.CSSProperties["fontWeight"],
      textTransform: sizeSelectorItem[type]
        .textTransform as React.CSSProperties["textTransform"],
      border: `${borderWidth} solid ${sizeSelectorItem[type].borderColor}`,
      height: `calc(${padding} * 3)`,
      fontFamily,
      letterSpacing: (sizeSelectorItem[type] as any)?.letterSpacing,
    };
    return bodyItemsStyles;
  };

  const titleTextTransform =
    titleStyles.textTransform as React.CSSProperties["textTransform"];

  const titlesStyles = {
    fontWeight: welcomeScreenUI.input_fields.title.fontWeight,
    textAlign: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["textAlign"],
    textTransform:
      titleTextTransform === "capitalize" ? "none" : titleTextTransform,
    color: welcomeScreenUI.input_fields.title
      .fontColor as React.CSSProperties["color"],
    fontSize: isMobile
      ? "14px"
      : (welcomeScreenUI.input_fields.title
          .fontSize as React.CSSProperties["fontSize"]),
    justifyContent: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["justifyContent"],
    fontStyle: (welcomeScreenUI.input_fields.title as any)
      .fontStyle as React.CSSProperties["fontStyle"],
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
  };

  const handleNextSelection = (
    listKey: "cup_sizes" | "band_sizes",
    activeIndex: number,
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>,
    setSelectedItem: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const list = BASE_SIZES.find((value: any) => value.name === sizeCountry)?.[
      listKey
    ];
    const nextIndex = list ? (activeIndex + 1) % list.length : -1;
    const selectedItem = list && nextIndex !== -1 ? list[nextIndex] : null;

    setActiveIndex(nextIndex);
    setSelectedItem(selectedItem);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === "Tab") {
      e.preventDefault();

      if (isActiveCupSelection) {
        handleNextSelection(
          "cup_sizes",
          activeCupIndex,
          setActiveCupIndex,
          setSelectedCup
        );
      } else {
        handleNextSelection(
          "band_sizes",
          activeSizeIndex,
          setActiveSizeIndex,
          setSelectedSize
        );
      }
    }

    if (e.key === "Enter") {
      if (!selectedCup) {
        setIsActiveCupSelection(true);
      } else if (
        typeof selectedSizeFR === "number" &&
        typeof selectedCupFR === "string"
      ) {
        nextStep();
      }
    }
  };

  return isShoesProduct ? (
    <ShoesComponentSize
      step={step}
      value_1={value_1}
      value_2={value_2}
      nextStep={nextStep}
      sizeCountry={sizeCountry}
      setSizeCountry={setSizeCountry}
      subtitleStyles={subtitleStyles}
    />
  ) : (
    <div className="breasts" onKeyDown={handleKeyDown}>
      {brandDefined?.name !== "Victoria Beckham" ? (
        <p
          className="breasts-description"
          style={{
            ...subtitleStyles,
            textTransform:
              subtitleStyles.textTransform === "capitalize"
                ? "none"
                : (subtitleStyles.textTransform as React.CSSProperties["textTransform"]),
            marginBottom: isMobile ? "25px" : "15px",
            marginTop:
              isMobile && brandDefined?.name !== "SRP"
                ? "0"
                : !isMobile && brandDefined?.name === "SRP"
                ? "20px"
                : "25px",
          }}
        >
          {subtitleStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("description"))
            : t("description")}
        </p>
      ) : null}
      <div
        className="breasts__titles"
        style={{
          display: "flex",
        }}
      >
        <p className="breasts__titles__title" style={titlesStyles}>
          {titleTextTransform === "capitalize"
            ? capitalizeFirstLetter(t("pays"))
            : t("pays")}
        </p>
        <div onClick={() => setOpen(!open)}>
          {brandDefined?.name === "SRP" ? (
            <StyledSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          ) : (
            <BaseSizeSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          )}
        </div>
      </div>
      <div className="breasts__titles">
        <p className="breasts__titles__title" style={titlesStyles}>
          {titleTextTransform === "capitalize"
            ? capitalizeFirstLetter(t("size"))
            : t("size")}
        </p>
      </div>
      <div className="breasts__body">
        <table className="breasts__body__table">
          <tbody>
            <tr>
              {BASE_SIZES.find(
                (value: any) => value.name === sizeCountry
              )?.band_sizes.map((row: any) => (
                <td key={row?.size}>
                  <button
                    className={`breasts__body__table__cell ${
                      selectedSize && selectedSize?.size === row?.size
                        ? "breasts__body__table__cell--selected"
                        : undefined
                    }`}
                    onClick={() => setSelectedSize(row)}
                    style={Object.assign({
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width:
                        row?.size?.toString()?.length > 4
                          ? "auto"
                          : `calc(${
                              getBodyItemsStyles(
                                selectedSize?.size === row?.size
                                  ? "focused"
                                  : "unfocused"
                              ).padding
                            } * 3)`,
                      ...(selectedSize?.size === row?.size
                        ? getBodyItemsStyles("focused")
                        : getBodyItemsStyles("unfocused")),
                    })}
                  >
                    {row?.size}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        <div className="breasts__titles">
          <p
            className="breasts__titles__title"
            style={{
              ...titlesStyles,
              position: "relative",
              top: isMobile ? 0 : "10px",
            }}
          >
            {titleTextTransform === "capitalize"
              ? capitalizeFirstLetter(t("cup"))
              : t("cup")}
          </p>
        </div>
        <table className="breasts__body__table">
          <tbody>
            <tr>
              {BASE_SIZES.find(
                (value: any) => value.name === sizeCountry
              )?.cup_sizes.map((row) => (
                <td key={row?.size} className="breasts__body__table__test">
                  <button
                    className={`breasts__body__table__cell ${
                      selectedCup && selectedCup?.size === row?.size
                        ? "breasts__body__table__cell--selected"
                        : undefined
                    }`}
                    onClick={() => setSelectedCup(row)}
                    style={Object.assign({
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width:
                        row?.size?.toString()?.length > 4
                          ? "auto"
                          : `calc(${
                              getBodyItemsStyles(
                                selectedCup?.size === row?.size
                                  ? "focused"
                                  : "unfocused"
                              ).padding
                            } * 3)`,
                      ...(selectedCup?.size === row?.size
                        ? getBodyItemsStyles("focused")
                        : getBodyItemsStyles("unfocused")),
                    })}
                  >
                    {row?.size}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <button
        type="button"
        disabled={
          typeof selectedSizeFR !== "number" ||
          typeof selectedCupFR !== "string"
        }
        className="continue-button breasts-continue"
        onClick={() => {
          nextStep();
          // handleAnalytics("action", "continue", {
          //   key: "current_step",
          //   type: "STR",
          //   value: "6_BRA",
          // });
        }}
        style={{
          ...(typeof selectedSizeFR !== "number" ||
          typeof selectedCupFR !== "string"
            ? disabledStylesContinue
            : activeStylesContinue),
          position: "relative",
          marginBottom:
            !isMobile && brandDefined?.name === "Victoria Beckham"
              ? "60px"
              : isMobile
              ? "20px"
              : "0px",
          marginTop:
            !isMobile && brandDefined?.name === "Victoria Beckham"
              ? "60px"
              : isMobile
              ? "10px"
              : "30px",
          textTransform:
            activeStylesContinue.textTransform === "capitalize"
              ? "none"
              : (activeStylesContinue.textTransform as React.CSSProperties["textTransform"]),
          padding: brandDefined?.name === "Victoria Beckham" ? "7px" : "16px",
          height: brandDefined?.name === "Victoria Beckham" ? "30px" : "48px",
        }}
      >
        {brandDefined?.name === "Theodore"
          ? t("continue")
          : activeStylesContinue.textTransform === "capitalize"
          ? capitalizeFirstLetter(t("continue"))
          : t("continue")}
      </button>
      {!isLingerie && brandDefined?.name !== "Victoria Beckham" && (
        <span
          className="breasts-skip"
          onClick={() => {
            skipResult();
            handleAnalytics("action", "bra_skip", null);
          }}
          style={{
            color: femaleBraScreenUI.skip.fontColor,
            fontWeight: femaleBraScreenUI.skip.fontWeight,
            fontSize: femaleBraScreenUI.skip.fontSize,
            textDecoration: femaleBraScreenUI.skip.fontStyle,
            textTransform:
              brandDefined?.name === "Zadig & Voltaire" ? "uppercase" : "none",
            letterSpacing: (femaleBraScreenUI.skip as any)?.letterSpacing,
          }}
        >
          {t("skip")}
        </span>
      )}
      <MeasuredBy step={step} />
    </div>
  );
};

export default StepBreasts;
